import { t } from '@lingui/core/macro';
import { OUTPUT_HEIGHT, OUTPUT_WIDTH } from 'modules/match-video/domain/assembler/constants';
import { ReviewCanvasContext, ReviewScene } from 'modules/match-video/domain/interfaces';
import {
  baselineFromTop,
  browserSceneEnv,
  drawContained,
  drawRisingBlock,
  fontMetrics,
  measureBlock,
  px,
  releaseSceneImage,
  SceneCanvas,
  SceneEnv,
} from 'modules/match-video/domain/scenes/kit';
import { IntroSceneData } from 'modules/match-video/domain/scenes/scene-data';
import { NIGHT_INTRO_SEC, nightIntroState, scoreColumns } from './intro-state';
import { FRAME_CENTER_X, limeAlpha, NIGHT, NIGHT_FONT_FACES, NIGHT_FONTS, shadowAlpha } from './style';

// Mockup sizes. Minimum sizes come from real data (Eliga, phase 6 journal): the longest names shrink to them, then «…».
const ORG = { right: OUTPUT_WIDTH - 96, top: 72, size: 26, min: 18, maxWidth: 720 };
const CHAMP = { left: 160, width: OUTPUT_WIDTH - 320, top: 118, size: 34, min: 26, lineHeight: 1.25, spacing: 0.5 };
const ROUND = { top: 200, size: 28, min: 20, maxWidth: 1200, padX: 26, padY: 10, spacing: 1 };
const TEAM = { width: 620, top: 330, logo: 250, logoImage: 190, logoGap: 32, size: 44, min: 34, lineHeight: 1.15 };
const TEAM_CENTERS = { home: 60 + TEAM.width / 2, away: OUTPUT_WIDTH - 60 - TEAM.width / 2 };
const SCORE = { top: 400, size: 150, spacing: -2 };
const FOOT = { bottom: 96, size: 30, min: 22, maxWidth: 1600 };

/** `.a-beam`: a 6000 × 220 band rotated −12° around (960, 410), far longer than the frame so its ends never show. */
const BEAM = { length: 6000, height: 220, cx: 960, cy: 410, angle: (-12 * Math.PI) / 180, glossLength: 1800 };
const GRAIN_OPACITY = 0.07;

/** Room around the logo disc for its drop shadow (blur 60 px, 24 px down). */
const LOGO_LAYER_PAD = 130;
const LOGO_LAYER_SIZE = TEAM.logo + 2 * LOGO_LAYER_PAD;

/**
 * `.a-team .logo` drawn once into its own layer: a white disc with a lime ring and a soft drop shadow, the logo
 * fitted inside. The layer is faded as a whole, like CSS `opacity` on the element — fading each part on its own would
 * let the white show through the logo while it flies in.
 */
function renderLogoLayer(env: SceneEnv, image: HTMLImageElement | null): SceneCanvas {
  const layer = env.createCanvas(LOGO_LAYER_SIZE, LOGO_LAYER_SIZE);
  const { ctx } = layer;
  const c = LOGO_LAYER_SIZE / 2;
  const r = TEAM.logo / 2;
  // Painted bottom-up like the box-shadow list: drop shadow, then the 8 px ring, then the disc.
  ctx.shadowColor = shadowAlpha(0.5);
  ctx.shadowBlur = 60;
  ctx.shadowOffsetY = 24;
  ctx.fillStyle = NIGHT.white;
  ctx.beginPath();
  ctx.arc(c, c, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.beginPath();
  ctx.arc(c, c, r + 8, 0, Math.PI * 2);
  ctx.arc(c, c, r, 0, Math.PI * 2, true);
  ctx.fillStyle = limeAlpha(0.25);
  ctx.fill();
  if (image) {
    const size = TEAM.logoImage;
    drawContained(ctx, image, c - size / 2, c - size / 2, size, size);
  }
  return layer;
}

function drawLogoLayer(ctx: ReviewCanvasContext, layer: SceneCanvas, cx: number, cy: number, dx: number, alpha: number): void {
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.drawImage(layer.canvas, cx + dx - LOGO_LAYER_SIZE / 2, cy - LOGO_LAYER_SIZE / 2);
  ctx.restore();
}

function drawBackground(ctx: ReviewCanvasContext, alpha: number): void {
  if (alpha <= 0) return;
  // `radial-gradient(1200px 700px at 50% 38%, inner 0%, outer 70%)`: an ellipse — a circle squeezed vertically.
  const cx = OUTPUT_WIDTH / 2;
  const cy = OUTPUT_HEIGHT * 0.38;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(cx, cy);
  ctx.scale(1, 700 / 1200);
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 1200);
  gradient.addColorStop(0, NIGHT.bgInner);
  gradient.addColorStop(0.7, NIGHT.bgOuter);
  gradient.addColorStop(1, NIGHT.bgOuter);
  ctx.fillStyle = gradient;
  // In the squeezed space the frame is taller; a generous rect covers it under the push-in too.
  ctx.fillRect(-cx - 100, -cy * (1200 / 700) - 200, OUTPUT_WIDTH + 200, (OUTPUT_HEIGHT * 1200) / 700 + 400);
  ctx.restore();
}

function drawBeam(ctx: ReviewCanvasContext, alpha: number, shiftY: number, glossX: number): void {
  if (alpha <= 0) return;
  const half = BEAM.length / 2;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(BEAM.cx, BEAM.cy);
  ctx.rotate(BEAM.angle);
  ctx.translate(0, shiftY);
  ctx.fillStyle = limeAlpha(0.045);
  ctx.fillRect(-half, -BEAM.height / 2, BEAM.length, BEAM.height);
  // The highlight layer: 1800 px wide, sliding along the band; no-repeat, so outside it nothing is added.
  const start = glossX - half;
  const gloss = ctx.createLinearGradient(start, 0, start + BEAM.glossLength, 0);
  gloss.addColorStop(0, limeAlpha(0));
  gloss.addColorStop(0.5, limeAlpha(0.16));
  gloss.addColorStop(1, limeAlpha(0));
  ctx.fillStyle = gloss;
  ctx.fillRect(Math.max(start, -half), -BEAM.height / 2, Math.min(BEAM.glossLength, half - start), BEAM.height);
  ctx.restore();
}

/**
 * Set A intro «Ночной стадион», 4 s: the background fades in, a floodlight beam drifts across, the championship rises
 * from under a line, the stage chip pops, logos fly in from the sides, names rise, the score slams and counts up,
 * the date spreads in from wide tracking; the whole frame slowly pushes in.
 *
 * Built once (fonts, logos, grain, fitted texts) — `draw` only paints.
 */
export async function createNightStadiumIntro(data: IntroSceneData, env: SceneEnv = browserSceneEnv): Promise<ReviewScene> {
  // Tabular figures, as `font-variant-numeric: tabular-nums` of the mockup: canvas cannot switch them on itself.
  const [, tabular, homeImage, awayImage] = await Promise.all([
    env.loadFonts(NIGHT_FONT_FACES),
    env.loadTabularFont('Unbounded', 900),
    env.loadImage(data.home.logoUrl),
    env.loadImage(data.away.logoUrl),
  ]);
  const grain = env.createGrain();
  const homeLogo = renderLogoLayer(env, homeImage);
  const awayLogo = renderLogoLayer(env, awayImage);
  releaseSceneImage(homeImage);
  releaseSceneImage(awayImage);
  const scoreFont = tabular ? `900 ${SCORE.size}px "${tabular}"` : NIGHT_FONTS.score(SCORE.size);
  const measurer = env.createCanvas(1, 1);
  const m = measurer.ctx;

  const org = data.organizerName
    ? measureBlock(m, data.organizerName.toLocaleUpperCase(), {
        maxWidth: ORG.maxWidth,
        size: ORG.size,
        min: ORG.min,
        font: NIGHT_FONTS.org,
      })
    : null;
  const champ = measureBlock(m, data.championshipName, {
    maxWidth: CHAMP.width,
    size: CHAMP.size,
    min: CHAMP.min,
    font: NIGHT_FONTS.champ,
    maxLines: 2,
    lineHeight: CHAMP.lineHeight,
    spacing: CHAMP.spacing,
  });
  const round = data.roundName
    ? measureBlock(m, data.roundName.toLocaleUpperCase(), {
        maxWidth: ROUND.maxWidth,
        size: ROUND.size,
        min: ROUND.min,
        font: NIGHT_FONTS.round,
        spacing: ROUND.spacing,
      })
    : null;
  const teamName = (name: string) =>
    measureBlock(m, name, {
      maxWidth: TEAM.width,
      size: TEAM.size,
      min: TEAM.min,
      font: NIGHT_FONTS.teamName,
      maxLines: 2,
      lineHeight: TEAM.lineHeight,
    });
  const homeName = teamName(data.home.name);
  const awayName = teamName(data.away.name);

  m.font = scoreFont;
  m.letterSpacing = px(SCORE.spacing);
  const scoreBaseline = baselineFromTop(SCORE.top, fontMetrics(m, SCORE.size), SCORE.size);
  const colonWidth = m.measureText(':').width;
  m.letterSpacing = '0px';

  const footText = [data.dateLabel, t`Видеообзор`].filter(Boolean).join(' · ').toLocaleUpperCase();
  // Fitted at its final tracking (2 px); the wider tracking of the entrance is allowed to overflow while it fades in.
  const foot = measureBlock(m, footText, {
    maxWidth: FOOT.maxWidth,
    size: FOOT.size,
    min: FOOT.min,
    font: NIGHT_FONTS.foot,
    spacing: 2,
  });
  measurer.release();

  const columns = scoreColumns(colonWidth);
  const nameTop = TEAM.top + TEAM.logo + TEAM.logoGap;
  const logoCenterY = TEAM.top + TEAM.logo / 2;
  const scoreCenterY = SCORE.top + SCORE.size / 2;
  const footTop = OUTPUT_HEIGHT - FOOT.bottom - foot.lineHeight;

  return {
    durationSec: NIGHT_INTRO_SEC,
    draw: (ctx, timeSec) => {
      const s = nightIntroState(timeSec, data.homeScore, data.awayScore);
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = 'left';

      ctx.translate(OUTPUT_WIDTH / 2, OUTPUT_HEIGHT / 2);
      ctx.scale(s.pushScale, s.pushScale);
      ctx.translate(-OUTPUT_WIDTH / 2, -OUTPUT_HEIGHT / 2);

      drawBackground(ctx, s.bgAlpha);
      drawBeam(ctx, s.beam.alpha, s.beam.shiftY, s.beam.glossX);
      grain.draw(ctx, GRAIN_OPACITY, OUTPUT_WIDTH, OUTPUT_HEIGHT);

      if (org && s.orgAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = s.orgAlpha;
        ctx.fillStyle = NIGHT.white;
        ctx.font = org.font;
        ctx.fillText(org.lines[0], ORG.right - org.widths[0], ORG.top + org.baselineOffset);
        ctx.restore();
      }

      ctx.save();
      ctx.fillStyle = NIGHT.champ;
      drawRisingBlock(ctx, champ, { x: FRAME_CENTER_X, top: CHAMP.top, width: CHAMP.width, align: 'center' }, s.champRise);
      ctx.restore();

      if (round && s.roundScale > 0) {
        const width = round.widths[0] + 2 * ROUND.padX;
        const height = round.lineHeight + 2 * ROUND.padY;
        const left = FRAME_CENTER_X - width / 2;
        // `translateX(-50%)` and `scale` share the origin of the untranslated box, so the chip grows from its right
        // end — kept as approved in the mockup.
        const originX = FRAME_CENTER_X + width / 2;
        const originY = ROUND.top + height / 2;
        ctx.save();
        ctx.translate(originX, originY);
        ctx.scale(s.roundScale, s.roundScale);
        ctx.translate(-originX, -originY);
        ctx.fillStyle = NIGHT.lime;
        ctx.beginPath();
        ctx.roundRect(left, ROUND.top, width, height, height / 2);
        ctx.fill();
        ctx.fillStyle = NIGHT.ink;
        ctx.letterSpacing = px(ROUND.spacing);
        ctx.font = round.font;
        ctx.fillText(round.lines[0], left + ROUND.padX, ROUND.top + ROUND.padY + round.baselineOffset);
        ctx.restore();
      }

      drawLogoLayer(ctx, homeLogo, TEAM_CENTERS.home, logoCenterY, s.homeLogo.dx, s.homeLogo.alpha);
      drawLogoLayer(ctx, awayLogo, TEAM_CENTERS.away, logoCenterY, s.awayLogo.dx, s.awayLogo.alpha);

      ctx.save();
      ctx.fillStyle = NIGHT.white;
      drawRisingBlock(ctx, homeName, { x: TEAM_CENTERS.home, top: nameTop, width: TEAM.width, align: 'center' }, s.homeNameRise);
      drawRisingBlock(ctx, awayName, { x: TEAM_CENTERS.away, top: nameTop, width: TEAM.width, align: 'center' }, s.awayNameRise);
      ctx.restore();

      if (s.score.alpha > 0) {
        ctx.save();
        ctx.globalAlpha = s.score.alpha;
        ctx.translate(FRAME_CENTER_X, scoreCenterY);
        ctx.scale(s.score.scale, s.score.scale);
        ctx.translate(-FRAME_CENTER_X, -scoreCenterY);
        ctx.font = scoreFont;
        ctx.letterSpacing = px(SCORE.spacing);
        const home = s.score.homeShown === null ? '–' : String(s.score.homeShown);
        const away = s.score.awayShown === null ? '–' : String(s.score.awayShown);
        ctx.fillStyle = NIGHT.white;
        ctx.fillText(home, columns.homeRight - ctx.measureText(home).width, scoreBaseline);
        ctx.fillText(away, columns.awayLeft, scoreBaseline);
        ctx.fillStyle = NIGHT.lime;
        ctx.fillText(':', columns.colonLeft, scoreBaseline);
        ctx.restore();
      }

      if (s.foot.alpha > 0) {
        ctx.save();
        ctx.globalAlpha = s.foot.alpha;
        ctx.fillStyle = NIGHT.foot;
        ctx.font = foot.font;
        ctx.letterSpacing = px(s.foot.letterSpacing);
        const width = ctx.measureText(foot.lines[0]).width;
        ctx.fillText(foot.lines[0], FRAME_CENTER_X - width / 2, footTop + foot.baselineOffset);
        ctx.restore();
      }
    },
    dispose: () => {
      grain.dispose();
      homeLogo.release();
      awayLogo.release();
    },
  };
}
